import { datadogRum } from "@datadog/browser-rum";
import { SavedFiltersProps, useFiltersV2Actions, useFiltersV2Select } from "PFApp/components/filters_v2";
import { ActionIcon } from "PFComponents/action_icon";
import { Button } from "PFComponents/button";
import { Inline } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { Pill } from "PFComponents/pill";
import { SavedFiltersV2 } from "PFTypes/saved_filters";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DD_SAVED_FILTER_APPLY } from "../datadog_actions";
import { isSavedFiltersCollectionSelected } from "../helpers";
import { useIsProcessingSavedFiltersCollections } from "../hooks/use_is_processing_saved_filters_collections";
import css from "./saved_filters_collection_badge.module.scss";

type SavedFiltersCollectionBadgeProps = {
  savedFilters: SavedFiltersV2;
  onShare: (id: SavedFiltersV2["id"], name: string) => void;
  onDelete: (id: SavedFiltersV2["id"], name: string) => void;
} & SavedFiltersProps;

export const SavedFiltersCollectionBadge = ({
  savedFilters,
  onShare,
  onDelete,
  omit
}: SavedFiltersCollectionBadgeProps) => {
  const { t } = useTranslation("filters", { keyPrefix: "savedFilters" });

  const { buffer: filtersMeta } = useFiltersV2Select("buffer", "collection");
  const { resetBufferValues } = useFiltersV2Actions();

  const isProcessing = useIsProcessingSavedFiltersCollections();

  const isSelected = useMemo(
    () => isSavedFiltersCollectionSelected(filtersMeta, savedFilters?.collection, omit),
    [filtersMeta, savedFilters.collection, omit]
  );

  const handleDelete = useCallback(() => {
    onDelete(savedFilters.id, savedFilters.name);
  }, [onDelete, savedFilters.id, savedFilters.name]);

  const handleApply = useCallback(() => {
    datadogRum.addAction(DD_SAVED_FILTER_APPLY, {
      id: savedFilters.id,
      name: savedFilters.name
    });

    resetBufferValues({ defaults: savedFilters.collection, ignoreFilterDefaultValues: true });
  }, [resetBufferValues, savedFilters.collection, savedFilters.id, savedFilters.name]);

  const handleShare = useCallback(() => {
    onShare(savedFilters.id, savedFilters.name);
  }, [onShare, savedFilters.id, savedFilters.name]);

  return (
    <Pill
      kind={isSelected ? "regular" : "ghost"}
      onDeleteIcon="remove"
      onDelete={handleDelete}
      disabled={isProcessing}
      classes={{ root: css.badge }}
    >
      <Inline alignItems="center">
        <Button kind="blank" onClick={handleApply} text={savedFilters.name} small />
        {!isProcessing && <ActionIcon title={t("share")} name="share" size="xxs" onClick={handleShare} />}
        {isProcessing && <LoadingDots />}
      </Inline>
    </Pill>
  );
};
